/**
 * Назначение: подписи и форматирование платежей в клиентском виджете.
 * Где: история платежей (`/pay/billing-history`), карточки оплат.
 */

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: 'Ожидает',
  processing: 'В обработке',
  succeeded: 'Оплачено',
  cancelled: 'Отменён',
  failed: 'Ошибка',
  refunded: 'Возврат'
};

const PAYMENT_PROVIDER_LABELS: Record<string, string> = {
  yookassa: 'ЮKassa',
  robokassa: 'Robokassa',
  tochka: 'Точка',
  manual: 'Вручную',
  stub: 'Служебный'
};

const amountFormatters = new Map<string, Intl.NumberFormat>();

function normalizeCurrency(currency: string | null | undefined): string {
  const cur = (currency ?? '').trim().toUpperCase();
  return cur || 'RUB';
}

function amountFormatter(currency: string): Intl.NumberFormat {
  const cached = amountFormatters.get(currency);
  if (cached) return cached;
  const formatter = new Intl.NumberFormat('ru-RU', { style: 'currency', currency });
  amountFormatters.set(currency, formatter);
  return formatter;
}

/** Сумма из копеек: «2 000,00 ₽»; неизвестная валюта — число с кодом валюты. */
export function formatPaymentAmount(cents: number | null | undefined, currency?: string | null): string {
  const cur = normalizeCurrency(currency);
  const amount = (cents ?? 0) / 100;
  try {
    return amountFormatter(cur).format(amount);
  } catch {
    return `${new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ${cur}`;
  }
}

/** Подпись статуса платежа; неизвестный код не показываем как есть. */
export function paymentStatusLabel(status: string | null | undefined): string {
  const key = (status ?? '').trim().toLowerCase();
  return PAYMENT_STATUS_LABELS[key] ?? 'Статус уточняется';
}

/** Подпись провайдера платежа; неизвестный код оставляем как есть. */
export function paymentProviderLabel(provider: string | null | undefined): string {
  const raw = (provider ?? '').trim();
  if (!raw) return '—';
  return PAYMENT_PROVIDER_LABELS[raw.toLowerCase()] ?? raw;
}

/** Дата и время платежа для карточки: «15.01.2026, 12:00». */
export function formatPaymentDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}
